import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Spinner } from 'react-bootstrap';
import { 
  faSackDollar, 
  faWallet,
  faMoneyBillTransfer
} from '@fortawesome/free-solid-svg-icons';
import { currencyFormat } from 'helpers/utils';
import 'assets/scss/orders/animations.scss';
import TransactionSummaryCard from 'components/cards/TransactionSummaryCard';

interface TransactionKpiCardsProps {
  orderTotals: {
    total_sales: number;
    total_commission: number;
    total_earnings: number;
    total_payment: number;
    total_withdrawal: number; // Added this property to fix the TypeScript error
  } | null;
  loading?: boolean;
}


/**
 * TransactionKpiCards - Displays three transparent summary cards with expandable calculation details:
 * 1. Total Earnings (with calculation: sales - commission)
 * 2. Total Paid (simple value)
 * 3. Withdrawal (with calculation: earnings - payment)
 */
const TransactionKpiCards: React.FC<TransactionKpiCardsProps> = ({
  orderTotals,
  loading = false
}) => {
  const { t } = useTranslation();
  // Animation states for sequential animations
  const [animateCards, setAnimateCards] = useState({
    container: false,
    earnings: false,
    paid: false,
    withdrawal: false
  });
  
  // Trigger sequential animations on component mount
  useEffect(() => {
    // Sequential animations with small delays
    const containerTimer = setTimeout(() => {
      setAnimateCards(prev => ({ ...prev, container: true }));
    }, 100);
    
    const earningsTimer = setTimeout(() => {
      setAnimateCards(prev => ({ ...prev, earnings: true }));
    }, 250);
    
    const paidTimer = setTimeout(() => {
      setAnimateCards(prev => ({ ...prev, paid: true }));
    }, 400);
    
    const withdrawalTimer = setTimeout(() => {
      setAnimateCards(prev => ({ ...prev, withdrawal: true }));
    }, 550);
    
    return () => {
      clearTimeout(containerTimer);
      clearTimeout(earningsTimer);
      clearTimeout(paidTimer);
      clearTimeout(withdrawalTimer);
    };
  }, []);
  


  // Default values for null orderTotals
  const safeOrderTotals = orderTotals || {
    total_sales: 0,
    total_commission: 0,
    total_earnings: 0,
    total_payment: 0,
    total_withdrawal: 0
  };

  // Calculation subtitle for Total Earnings
  const earningsCalculation = (
    <div className="pt-1 border-top border-light">
      <p className="text-body-secondary fs-9 mb-1" aria-label="Earnings calculation formula">
        <span className="text-primary fw-medium">
          <span className="currency-value">{currencyFormat(safeOrderTotals.total_sales)}</span>
        </span>
        <span className="mx-1 opacity-75">−</span>
        <span className="text-danger fw-medium">
          <span className="currency-value">{currencyFormat(safeOrderTotals.total_commission)}</span>
        </span>
        <span className="mx-1 opacity-75">=</span>  
        <span className="text-info fw-semibold">
          <span className="currency-value">{currencyFormat(safeOrderTotals.total_earnings)}</span>
        </span>
      </p>
      <p className="text-body-tertiary fs-10 mb-0 opacity-75">
        <small>{t("transactions.kpi.earningsFormula", "Total Sales − Total Commission = Total Earnings")}</small>
      </p>
    </div>
  );

  // Calculation subtitle for Withdrawal
  const withdrawalCalculation = (
    <div className="pt-1 border-top border-light">
      {/* <p className="text-body-secondary fs-9 mb-1" aria-label="Withdrawal calculation formula">
        <span className="text-info fw-medium">
          <span className="currency-value">{currencyFormat(orderTotals.total_earnings)}</span>
        </span>
        <span className="mx-1 opacity-75">−</span>
        <span className="text-success fw-medium">
          <span className="currency-value">{currencyFormat(orderTotals.total_payment)}</span>
        </span>
        <span className="mx-1 opacity-75">=</span>
        <span className="text-warning fw-semibold">
          <span className="currency-value">{currencyFormat(orderTotals.total_withdrawal)}</span>
        </span>
      </p> */}
      <p className="text-body-tertiary fs-10 mb-0 opacity-75">
        <small>{t("transactions.kpi.withdrawalFormula", "Total Earnings − Total Paid = Withdrawal")}</small>
      </p>
    </div>
  );

  return (
    <div className="mb-4">
      {/* Grid layout for cards to enable independent expansion */}
      <div 
        className={`transaction-cards-grid ${animateCards.container ? 'fade-in' : ''}`}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          alignItems: 'start' // Important for independent card heights
        }}
      >
        {/* Total Earnings Card */}
        <div className={animateCards.earnings ? 'fade-in' : 'opacity-0'} style={{ transition: 'opacity 0.5s ease-in-out' }}>
          <TransactionSummaryCard
            key="earnings-card"
            title={t("transactions.kpi.totalEarnings", "Total Earnings")}
            amount={safeOrderTotals.total_earnings}
            calculation={earningsCalculation}
            icon={faSackDollar}
            iconColorClass="text-success"
            iconBgClass="bg-success-subtle"
            valueColorClass="text-success"
            ariaLabel="Total earnings amount"
            hasCalculation={true}
            loading={loading}
          />
        </div>

        {/* Total Paid Card */}
        <div className={animateCards.paid ? 'fade-in' : 'opacity-0'} style={{ transition: 'opacity 0.5s ease-in-out' }}>
          <TransactionSummaryCard
            key="paid-card"
            title={t("transactions.kpi.totalPaid", "Total Paid")}
            amount={safeOrderTotals.total_payment}
            icon={faWallet}
            iconColorClass="text-info"
            iconBgClass="bg-info-subtle"
            valueColorClass="text-info"
            ariaLabel="Total paid amount"
            hasCalculation={false}
            loading={loading}
          />
        </div>

        {/* Withdrawal Card */}
        <div className={animateCards.withdrawal ? 'fade-in' : 'opacity-0'} style={{ transition: 'opacity 0.5s ease-in-out' }}>
          <TransactionSummaryCard
            key="withdrawal-card"
            title={t("transactions.kpi.withdrawal", "Withdrawal")}
            amount={safeOrderTotals.total_withdrawal}
            calculation={withdrawalCalculation}
            icon={faMoneyBillTransfer}
            iconColorClass="text-warning"
            iconBgClass="bg-warning-subtle"
            valueColorClass="text-warning"
            ariaLabel="Withdrawal amount available"
            hasCalculation={true}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionKpiCards;
